import React from "react";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { DisclosureBanner } from "./DisclosureBanner";
import { CTAButton } from "./CTAButton";
import { Breadcrumbs } from "./Breadcrumbs";
import { RelatedReports } from "./RelatedReports";
import { useSEO, SITE } from "../lib/useSEO";

const buildSchemas = (cfg) => {
    const canonical = `${SITE.origin}${cfg.slug}`;
    return [
        {
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: cfg.title,
            url: canonical,
            description: cfg.description,
            isPartOf: {
                "@type": "WebSite",
                name: SITE.name,
                url: SITE.origin,
            },
            inLanguage: "en-US",
        },
        {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: cfg.h1,
            description: cfg.description,
            mainEntityOfPage: canonical,
            inLanguage: "en-US",
            articleSection: "Sleep Comfort",
            keywords: cfg.keywords,
            image: cfg.image.src,
            author: { "@type": "Organization", name: SITE.name },
            publisher: {
                "@type": "Organization",
                name: SITE.name,
                url: SITE.origin,
            },
        },
        {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
                {
                    "@type": "ListItem",
                    position: 1,
                    name: "Home",
                    item: SITE.origin + "/",
                },
                {
                    "@type": "ListItem",
                    position: 2,
                    name: cfg.breadcrumb,
                    item: canonical,
                },
            ],
        },
        {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: cfg.faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
        },
    ];
};

export const MiniLandingPage = ({ config: cfg }) => {
    useSEO({
        title: cfg.title,
        description: cfg.description,
        path: cfg.slug,
        ogImage: cfg.image.src,
        schemas: buildSchemas(cfg),
    });

    const slugId = cfg.slug.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "");

    return (
        <div className="App">
            <DisclosureBanner variant="top" />
            <Header />

            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-20 pb-16 md:pb-24">
                <Breadcrumbs
                    testId={`breadcrumbs-${slugId}`}
                    items={[
                        { label: "Home", href: "/" },
                        { label: cfg.breadcrumb, href: cfg.slug },
                    ]}
                />

                <span className="brr-overline">{cfg.kicker}</span>
                <h1
                    data-testid={`h1-${slugId}`}
                    className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-5 mb-6"
                >
                    {cfg.h1}
                </h1>
                <p className="text-lg sm:text-xl font-light leading-relaxed text-[color:var(--brr-ink-2)] max-w-2xl">
                    {cfg.intro}
                </p>

                <figure className="mt-10 md:mt-14">
                    <img
                        data-testid={`hero-image-${slugId}`}
                        src={cfg.image.src}
                        alt={cfg.image.alt}
                        loading="eager"
                        className="w-full h-auto max-h-[60vh] object-contain rounded-2xl bg-[color:var(--brr-bg-accent)]"
                    />
                </figure>

                <div className="mt-10 flex flex-col sm:flex-row gap-5 items-start sm:items-center">
                    <CTAButton
                        label="Check Today's Offer"
                        testId={`cta-top-${slugId}`}
                    />
                    <CTAButton
                        variant="ghost"
                        label="Read The Better Rest Report"
                        testId={`cta-top-read-${slugId}`}
                    />
                </div>

                <article className="legal-prose mt-14">
                    {cfg.body.map((block, i) => (
                        <section
                            key={block.heading}
                            data-testid={`section-${slugId}-${i}`}
                            className="mt-10"
                        >
                            <h2>{block.heading}</h2>
                            {block.paragraphs?.map((p, j) => (
                                <p key={j}>{p}</p>
                            ))}
                            {block.bullets ? (
                                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
                                    {block.bullets.map((b, k) => (
                                        <div
                                            key={b.title}
                                            data-testid={`bullet-${slugId}-${i}-${k}`}
                                            className="bg-white border border-[color:var(--brr-border)] rounded-2xl p-6"
                                        >
                                            <h3 className="font-serif-display text-lg sm:text-xl mb-2">
                                                {b.title}
                                            </h3>
                                            <p className="text-[15px] leading-relaxed font-light text-[color:var(--brr-ink-2)]">
                                                {b.body}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            ) : null}
                        </section>
                    ))}

                    <section className="mt-14">
                        <h2>Reader questions</h2>
                        {cfg.faqs.map((f, idx) => (
                            <div
                                key={f.q}
                                data-testid={`faq-${slugId}-${idx}`}
                                className="mt-6"
                            >
                                <h3 className="text-xl font-serif-display mb-2">
                                    {f.q}
                                </h3>
                                <p>{f.a}</p>
                            </div>
                        ))}
                    </section>
                </article>

                <div className="mt-14 flex flex-col sm:flex-row gap-5 items-start sm:items-center">
                    <CTAButton
                        label="Check Today's Offer"
                        testId={`cta-bottom-${slugId}`}
                    />
                    <CTAButton
                        variant="ghost"
                        label="Read The Better Rest Report"
                        testId={`cta-bottom-read-${slugId}`}
                    />
                </div>
            </main>

            <RelatedReports exclude={cfg.slug} />
            <Footer />
        </div>
    );
};
